// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

// The gutter the count labels sit in, one either side of every plot, so their
// plots start at the same offset and a day lines up down the panel.
export const AXIS_WIDTH = 34;

// How hard the hovered day's column is tinted. Both charts light up the same
// day, so it has to carry across a plot that is already tinted behind its bars.
export const HOVER_TINT = 0.12;

// The steps an axis is allowed to climb in, as a share of a power of ten. A
// count is a whole number of units, so a step that is not one is dropped: 2.5
// becomes 25 and 250 but never 2.5.
const TICK_MULTIPLIERS = [1, 2, 2.5, 5, 10];

/** The top of a count axis and the values that label it, from zero up. */
export interface AxisScale {
  max: number;
  ticks: number[];
}

/**
 * An axis that stops on a round number rather than on the tallest bar.
 *
 * A busiest day of forty-six put forty-six at the top of the axis, which is a
 * reading nobody is looking for and gives the labels below it no round values
 * either. Rounding the step up to the nearest ten, twenty-five or hundred puts
 * the top at fifty instead, and every label under it lands somewhere a height
 * can actually be read off.
 *
 * targetIntervals is how many gaps the axis is aimed at rather than promised.
 * The step is what has to be round, so the count that comes back is close to it
 * rather than equal to it.
 */
export function toAxisScale(peak: number, targetIntervals: number): AxisScale {
  const highest = Math.max(peak, 1);
  const rough = highest / targetIntervals;
  const magnitude = Math.max(10 ** Math.floor(Math.log10(rough)), 1);
  const step =
    TICK_MULTIPLIERS.map((multiplier) => multiplier * magnitude).find(
      (candidate) => Number.isInteger(candidate) && candidate >= rough
    ) ?? magnitude * 10;

  const max = Math.ceil(highest / step) * step;
  const ticks: number[] = [];
  for (let value = 0; value <= max; value += step) {
    ticks.push(value);
  }
  return { max, ticks };
}

// The bars separate up to a month, carry a hairline up to six, and run flush
// past that, where the separation would cost more than it shows and the chart
// reads as an area instead. All three are fixed by the preset rather than
// measured, so a resize can never flip a chart between one look and the other.
const SOLID_FROM_DAYS = 180;
const WIDE_GAP_MAX_DAYS = 30;
const GAP_SHARE_OF_SLOT = 0.25;
const MAX_GAP = 8;

/**
 * The gap between two bars, in device pixels.
 *
 * A display scaled to 125% renders a one pixel gap as 1.25 device pixels and the
 * browser rounds each one on its own, so identical bars come out with gaps a
 * pixel apart. Every measurement here is a whole number of device pixels, which
 * is what keeps the row identical whatever the display is scaled to.
 */
function toDeviceGap(
  deviceAvailable: number,
  dayCount: number,
  pixelRatio: number
): number {
  if (dayCount >= SOLID_FROM_DAYS) return 0;

  const hairline = Math.max(Math.round(pixelRatio), 1);
  if (dayCount > WIDE_GAP_MAX_DAYS) return hairline;

  const slot = deviceAvailable / dayCount;
  return Math.min(
    Math.max(Math.round(slot * GAP_SHARE_OF_SLOT), hairline),
    Math.round(MAX_GAP * pixelRatio)
  );
}

/** Where the plot sits inside the frame and how wide each bar in it is. */
export interface PlotLayout {
  // The plot fills the frame between the two axes exactly, so there is no
  // margin to recompute and nothing that can shift as the window resizes.
  plotWidth: number;
  barWidth: number;
  gapWidth: number;
  // How many bars take one extra device pixel, spread across the plot, so the
  // bars fill the width the floored ones left short.
  wideBars: number;
  pixelRatio: number;
}

/**
 * One layout for every part of the panel, so the membership chart, the delta
 * chart under it and the scrub bar all put a day in the same place.
 *
 * The bars absorb whatever the division leaves over. Handing it back as a
 * margin instead made the plot's edges move with the day count, which read as
 * the chart flickering as the window changed.
 */
export function toPlotLayout(
  frameWidth: number,
  dayCount: number,
  pixelRatio: number
): PlotLayout {
  const available = Math.max(frameWidth - AXIS_WIDTH * 2, 0);
  if (dayCount === 0 || available <= 0) {
    return {
      plotWidth: 0,
      barWidth: 0,
      gapWidth: 0,
      wideBars: 0,
      pixelRatio,
    };
  }

  const deviceAvailable = Math.floor(available * pixelRatio);
  // A window longer than the plot is wide has no whole pixel to give each day.
  // The bars go sub-pixel and merge into an area rather than overflowing.
  if (deviceAvailable < dayCount) {
    return {
      plotWidth: available,
      barWidth: available / dayCount,
      gapWidth: 0,
      wideBars: 0,
      pixelRatio,
    };
  }

  // Every bar keeps at least one device pixel, so a very long window loses its
  // separation rather than its bars.
  const widestGap = Math.max(
    Math.floor((deviceAvailable - dayCount) / Math.max(dayCount - 1, 1)),
    0
  );
  const deviceGap = Math.min(
    toDeviceGap(deviceAvailable, dayCount, pixelRatio),
    widestGap
  );
  const gaps = deviceGap * (dayCount - 1);
  const deviceBar = Math.max(
    Math.floor((deviceAvailable - gaps) / dayCount),
    1
  );
  const wideBars = Math.max(deviceAvailable - (deviceBar * dayCount + gaps), 0);

  return {
    plotWidth: (deviceBar * dayCount + gaps + wideBars) / pixelRatio,
    barWidth: deviceBar / pixelRatio,
    gapWidth: deviceGap / pixelRatio,
    wideBars,
    pixelRatio,
  };
}

/** Where one day's bar sits across the plot, and how wide it is. */
export interface DayBand {
  left: number;
  width: number;
}

export function toDayBand(
  day: number,
  layout: PlotLayout,
  dayCount: number
): DayBand {
  const slot = layout.barWidth + layout.gapWidth;
  const spare = 1 / layout.pixelRatio;
  // The spare pixels are spread evenly rather than handed to the leading bars.
  // Front-loading them made the plot piecewise linear, so the scrub thumb, which
  // can only move linearly, drifted tens of pixels from its own day in the
  // middle of a year-long window.
  const before = Math.floor((day * layout.wideBars) / dayCount);
  const after = Math.floor(((day + 1) * layout.wideBars) / dayCount);
  return {
    left: day * slot + before * spare,
    width: layout.barWidth + (after > before ? spare : 0),
  };
}

/**
 * Which day the cursor is over, walked from the real bar widths.
 *
 * Dividing the plot evenly looks right and is not: the spare device pixels are
 * spread across the bars, so an even division drifts further from the truth the
 * further across the plot the cursor is.
 */
export function toDayAtOffset(
  offsetX: number,
  measuredWidth: number,
  layout: PlotLayout,
  dayCount: number
): number | null {
  if (dayCount === 0 || measuredWidth <= 0) return null;

  const slot = layout.barWidth + layout.gapWidth;
  // Nothing has been measured yet, so there is no geometry to walk.
  if (slot <= 0) {
    return toClampedDay(
      Math.floor(offsetX / (measuredWidth / dayCount)),
      dayCount
    );
  }

  // The spare pixels are spread across the plot, so an average slot lands
  // within a day of the answer and the walk settles it.
  let day = toClampedDay(
    Math.floor(
      offsetX / (slot + layout.wideBars / dayCount / layout.pixelRatio)
    ),
    dayCount
  );
  while (day > 0 && toDayBand(day, layout, dayCount).left > offsetX) {
    day -= 1;
  }
  while (
    day < dayCount - 1 &&
    toDayBand(day + 1, layout, dayCount).left <= offsetX
  ) {
    day += 1;
  }
  return day;
}

function toClampedDay(day: number, dayCount: number): number {
  return Math.min(Math.max(day, 0), dayCount - 1);
}
